import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Package, Buildings, Phone, EnvelopeSimple, Calendar, User, ArrowRight } from "@phosphor-icons/react";

const STATUS_LABELS = {
  new: { fr: "Nouvelle", color: "bg-blue-100 text-blue-700" },
  contacted: { fr: "Contactée", color: "bg-cyan-100 text-cyan-700" },
  demo_scheduled: { fr: "Démo planifiée", color: "bg-violet-100 text-violet-700" },
  evaluating: { fr: "En évaluation", color: "bg-amber-100 text-amber-700" },
  trial: { fr: "Essai", color: "bg-emerald-100 text-emerald-700" },
  offer_sent: { fr: "Offre envoyée", color: "bg-fuchsia-100 text-fuchsia-700" },
  accepted: { fr: "Acceptée", color: "bg-teal-100 text-teal-700" },
  activated: { fr: "Activée", color: "bg-emerald-200 text-emerald-800" },
  rejected: { fr: "Refusée", color: "bg-red-100 text-red-700" },
};

export default function SubscriptionRequests() {
  const [state, setState] = useState({ items: [], counts: {}, total: 0 });
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const q = statusFilter !== "all" ? `?status=${statusFilter}` : "";
    api.get(`/subscription-requests${q}`)
      .then(r => setState(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const updateStatus = async (req, newStatus) => {
    try {
      await api.patch(`/subscription-requests/${req.id}`, { status: newStatus });
      toast.success(`Statut → ${STATUS_LABELS[newStatus]?.fr || newStatus}`);
      load();
      if (selected?.id === req.id) setSelected({ ...selected, status: newStatus });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const convert = async (req) => {
    if (!confirm(`Créer un tenant DEMO pour "${req.school_name}" (30 jours d'essai) ?`)) return;
    try {
      const r = await api.post(`/subscription-requests/${req.id}/convert`);
      toast.success(`Établissement créé (${r.data.slug}) · Email d'activation envoyé à ${r.data.activation_url_sent_to}`);
      load();
      setSelected(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <header className="mb-6">
        <div className="text-xs uppercase tracking-widest text-secondary font-semibold">CRM commercial</div>
        <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
          <Package size={32} weight="duotone" className="text-primary" />
          Demandes & Souscriptions
        </h1>
      </header>

      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-6">
        {Object.entries(STATUS_LABELS).map(([k, v]) => (
          <button key={k} onClick={()=>setStatusFilter(k)}
                  className={`p-3 rounded-lg border text-left transition-colors ${statusFilter === k ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"}`}
                  data-testid={`filter-${k}`}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold truncate">{v.fr}</div>
            <div className="font-heading text-xl font-bold tabular-nums">{state.counts?.[k] || 0}</div>
          </button>
        ))}
      </div>
      <Button variant="ghost" size="sm" onClick={()=>setStatusFilter("all")} className="mb-4 text-xs" data-testid="btn-all">Voir toutes ({state.total})</Button>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Établissement</th>
                <th className="px-3 py-2 text-left">Contact</th>
                <th className="px-3 py-2 text-center">Type</th>
                <th className="px-3 py-2 text-center">Effectif</th>
                <th className="px-3 py-2 text-center">Statut</th>
                <th className="px-3 py-2 text-right">Date</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Chargement…</td></tr> :
               state.items.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Aucune demande.</td></tr> :
               state.items.map((it, i) => (
                <tr key={it.id} className={`border-t border-border cursor-pointer hover:bg-muted/30 ${i % 2 ? "bg-muted/10" : ""}`}
                    onClick={()=>setSelected(it)} data-testid={`req-${it.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium">{it.school_name}</div>
                    <div className="text-[10px] text-muted-foreground">{it.city}, {it.country}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div>{it.contact_first_name} {it.contact_last_name}</div>
                    <div className="text-[10px] text-muted-foreground font-mono">{it.contact_email}</div>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <Badge variant="outline" className="text-[10px]">{it.request_type === "demo" ? "Démo" : "Accès"}</Badge>
                  </td>
                  <td className="px-3 py-2 text-center text-xs tabular-nums">{it.student_count || "—"}</td>
                  <td className="px-3 py-2 text-center">
                    <Badge className={`${STATUS_LABELS[it.status]?.color || ""} text-[10px]`}>{STATUS_LABELS[it.status]?.fr || it.status}</Badge>
                  </td>
                  <td className="px-3 py-2 text-right text-[10px] text-muted-foreground whitespace-nowrap">
                    {new Date(it.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!selected} onOpenChange={o=>!o && setSelected(null)}>
        <DialogContent className="max-w-2xl" data-testid="dialog-request">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Buildings size={20} weight="duotone" className="text-primary" /> {selected.school_name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><User size={12} className="inline mr-1" /> {selected.contact_first_name} {selected.contact_last_name} · {selected.contact_role || "—"}</div>
                  <div><EnvelopeSimple size={12} className="inline mr-1" /> {selected.contact_email}</div>
                  <div><Phone size={12} className="inline mr-1" /> {selected.contact_phone || "—"} · WA {selected.contact_whatsapp || "—"}</div>
                  <div><Calendar size={12} className="inline mr-1" /> {new Date(selected.created_at).toLocaleString()}</div>
                </div>
                <div className="p-3 bg-muted/40 rounded-lg text-xs">
                  <div><b>Type:</b> {selected.school_type} · <b>Pays:</b> {selected.country} · <b>Ville:</b> {selected.city} · <b>Effectif:</b> {selected.student_count || "—"} élèves, {selected.teacher_count || "—"} enseignants</div>
                  {selected.modules_wanted?.length > 0 && <div className="mt-2"><b>Modules demandés:</b> {selected.modules_wanted.join(", ")}</div>}
                  {selected.message && <div className="mt-2 border-t border-border pt-2 whitespace-pre-wrap">{selected.message}</div>}
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Changer le statut</div>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(STATUS_LABELS).map(([k, v]) => (
                      <Button key={k} size="sm" variant={selected.status === k ? "default" : "outline"} onClick={()=>updateStatus(selected, k)} data-testid={`btn-status-${k}`}>{v.fr}</Button>
                    ))}
                  </div>
                </div>
                {selected.history?.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Historique</div>
                    <div className="space-y-1 text-[11px] max-h-40 overflow-y-auto">
                      {selected.history.slice().reverse().map((h, i) => (
                        <div key={i} className="flex gap-2">
                          <span className="text-muted-foreground">{new Date(h.at).toLocaleString()}</span>
                          <Badge className={`${STATUS_LABELS[h.status]?.color || ""} text-[9px]`}>{STATUS_LABELS[h.status]?.fr || h.status}</Badge>
                          {h.by && <span className="text-muted-foreground">par {h.by}</span>}
                          {h.note && <span>· {h.note}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                {!selected.converted_school_id && (
                  <Button onClick={()=>convert(selected)} className="bg-primary text-primary-foreground" data-testid="btn-convert">
                    <ArrowRight size={14} className="mr-1" /> Créer l'établissement (DEMO 30j)
                  </Button>
                )}
                {selected.converted_school_id && (
                  <div className="flex flex-col gap-1">
                    <Badge className="bg-emerald-100 text-emerald-700">✓ Converti · slug: {selected.converted_school_id.slice(0,8)}…</Badge>
                    <Button size="sm" variant="outline" onClick={async () => {
                      if (!confirm("Convertir cet établissement DEMO en tenant ACTIF ? Aucune donnée n'est perdue (élèves, notes, finances conservés).")) return;
                      try {
                        const r = await api.post(`/schools/${selected.converted_school_id}/convert-to-active`);
                        toast.success(`École passée de ${r.data.from} → ${r.data.to} ✓`);
                      } catch (e) { toast.error(formatApiError(e)); }
                    }} data-testid="btn-convert-to-active">
                      🚀 Convertir DEMO → ACTIVE
                    </Button>
                  </div>
                )}
                <Button variant="outline" onClick={()=>setSelected(null)}>Fermer</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
